import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import { applicationApi, type Application } from '../../../lib/api'
import { apiErrorMessage } from '../../../hooks/useErrorHandler'
import { SectionBody, SectionHeader } from './StageContextLayout'

export function ProcessorSubmissionContext({ app }: { app?: Application | null }) {
  const queryClient = useQueryClient()
  const [midDraft, setMidDraft] = useState(app?.fiserv_mid ?? '')
  const [midReason, setMidReason] = useState('')
  const [midMessage, setMidMessage] = useState<string | null>(null)
  const [midError, setMidError] = useState<string | null>(null)
  const [dryRunMessage, setDryRunMessage] = useState<string | null>(null)
  const [dryRunError, setDryRunError] = useState<string | null>(null)
  const [reboardReason, setReboardReason] = useState('')
  const [reboardMessage, setReboardMessage] = useState<string | null>(null)
  const [reboardError, setReboardError] = useState<string | null>(null)
  const isNorth = app?.platform === 'north'
  const northLatest = app?.north_boarding?.latest ?? null
  const declineReasons = [
    ...(northLatest?.decline_reasons ?? []),
    ...(app?.fiserv_status_errors ?? []).map((error) => error.message).filter((message): message is string => Boolean(message)),
  ]
  const hasSubmissionIssue = Boolean(
    app?.fiserv_submission_error ||
    app?.fiserv_status === 'submission_failed' ||
    (app?.fiserv_ext_ref_id && !app?.is_submitted && !app?.fiserv_as_ref_id),
  )
  const updateMid = useMutation({
    mutationFn: async () => {
      if (!app) throw new Error('No application')
      return applicationApi.updateFiservMid(app.id, {
        fiserv_mid: midDraft.trim(),
        reason: midReason.trim() || undefined,
      })
    },
    onSuccess: (updated) => {
      setMidError(null)
      setMidMessage(`Reserved MID updated to ${updated.fiserv_mid}.`)
      setMidReason('')
      setMidDraft(updated.fiserv_mid ?? '')
      if (updated.lead_id) {
        queryClient.invalidateQueries({ queryKey: ['lead', updated.lead_id] })
      }
      queryClient.invalidateQueries({ queryKey: ['leads'] })
    },
    onError: (error) => {
      setMidMessage(null)
      setMidError(apiErrorMessage(error, 'Could not update reserved MID.'))
    },
  })
  const dryRun = useMutation({
    mutationFn: async () => {
      if (!app) throw new Error('No application')
      return applicationApi.northDryRun(app.id)
    },
    onSuccess: (result) => {
      setDryRunError(null)
      const problems = [...result.missing_configuration, ...result.schema_errors]
      setDryRunMessage(result.valid
        ? 'North XML matches the production FACS schema. Nothing was sent.'
        : `North XML is not ready. ${problems.join(' ')}`)
    },
    onError: (error) => {
      setDryRunMessage(null)
      setDryRunError(apiErrorMessage(error, 'Could not validate the North XML.'))
    },
  })
  const reboard = useMutation({
    mutationFn: async () => {
      if (!app) throw new Error('No application')
      return applicationApi.northReboard(app.id, reboardReason.trim())
    },
    onSuccess: (result) => {
      setReboardError(null)
      setReboardMessage(result.message)
      setReboardReason('')
      if (app?.lead_id) {
        queryClient.invalidateQueries({ queryKey: ['lead', app.lead_id] })
      }
    },
    onError: (error) => {
      setReboardMessage(null)
      setReboardError(apiErrorMessage(error, 'Could not start a North reboard.'))
    },
  })

  return (
    <>
      <SectionHeader color="#f3e8ff">
        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#6b21a8' }}>Processor Submission</h3>
      </SectionHeader>
      <SectionBody>
        {isNorth && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '14px', fontWeight: 700, color: '#1e3a8a', marginBottom: '8px' }}>
              North boarding
            </p>
            <p style={{ fontSize: '13px', color: '#1f2937', marginBottom: '8px' }}>
              {app?.north_boarding?.send_enabled
                ? 'Live North send is enabled for this environment.'
                : 'Live North send is off. FISERV_NORTH_BOARDING_SEND_ENABLED is false, so dry-run does not send this application.'}
            </p>
            <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', marginBottom: '8px' }}>
              <div>
                <dt style={{ color: '#6b7280' }}>Latest status</dt>
                <dd style={{ color: '#111827' }}>{northLatest?.status ?? 'No North submission yet'}</dd>
              </div>
              <div>
                <dt style={{ color: '#6b7280' }}>Request ID</dt>
                <dd style={{ color: '#111827', fontFamily: 'monospace' }}>{northLatest?.request_id ?? '—'}</dd>
              </div>
            </dl>
            {declineReasons.length > 0 && (
              <ul style={{ margin: '0 0 8px', paddingLeft: '18px', color: '#991b1b', fontSize: '13px' }}>
                {declineReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            )}
            <Button
              type="button"
              onClick={() => dryRun.mutate()}
              isLoading={dryRun.isPending}
              disabled={dryRun.isPending}
            >
              Validate North XML
            </Button>
            {dryRunMessage && (
              <p style={{ fontSize: '12px', color: '#1f2937', marginTop: '8px' }}>{dryRunMessage}</p>
            )}
            {dryRunError && (
              <p style={{ fontSize: '12px', color: '#b91c1c', marginTop: '8px' }}>{dryRunError}</p>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault()
                reboard.mutate()
              }}
              style={{ marginTop: '12px' }}
            >
              <p style={{ fontSize: '12px', color: '#374151', marginBottom: '8px' }}>
                A funding-bank change, or an ExtRefID that Fiserv declined, is resubmitted from here. Reboard creates a new ExtRefID linked to the original submission.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px' }}>
                <Input
                  aria-label="North reboard reason"
                  value={reboardReason}
                  onChange={(event) => setReboardReason(event.target.value)}
                  placeholder="Why this needs a new ExtRefID"
                />
                <Button
                  type="submit"
                  isLoading={reboard.isPending}
                  disabled={reboard.isPending || reboardReason.trim().length < 10 || app?.north_boarding?.send_enabled !== true}
                >
                  Reboard
                </Button>
              </div>
              {reboardMessage && (
                <p style={{ fontSize: '12px', color: '#047857', marginTop: '8px' }}>{reboardMessage}</p>
              )}
              {reboardError && (
                <p style={{ fontSize: '12px', color: '#b91c1c', marginTop: '8px' }}>{reboardError}</p>
              )}
            </form>
          </div>
        )}
        {hasSubmissionIssue ? (
          <div>
            <p style={{ fontSize: '14px', color: '#dc2626', fontWeight: 700, marginBottom: '8px' }}>
              Fiserv Submission Did Not Complete
            </p>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              marginBottom: '12px',
            }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
                Failure Reason
              </p>
              <p style={{ fontSize: '14px', color: '#7f1d1d', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {app?.fiserv_submission_error ?? 'This submission attempt started and reserved a Fiserv reference, but no successful SubmitMPA acknowledgement was recorded.'}
              </p>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
              <div>
                <dt style={{ color: '#6b7280' }}>Reserved MID</dt>
                <dd style={{ color: '#111827', fontFamily: 'monospace' }}>{app?.fiserv_mid ?? '—'}</dd>
              </div>
              <div>
                <dt style={{ color: '#6b7280' }}>Ext Ref ID</dt>
                <dd style={{ color: '#111827', fontFamily: 'monospace' }}>{app?.fiserv_ext_ref_id ?? '—'}</dd>
              </div>
              <div>
                <dt style={{ color: '#6b7280' }}>Failed At</dt>
                <dd style={{ color: '#111827' }}>
                  {app?.fiserv_submission_failed_at ? new Date(app.fiserv_submission_failed_at).toLocaleString() : '—'}
                </dd>
              </div>
            </dl>
            {app && !app.is_submitted && (
              <form
                onSubmit={(event) => {
                  event.preventDefault()
                  updateMid.mutate()
                }}
                style={{
                  marginTop: '16px',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: '#f9fafb',
                }}
              >
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>
                  Correct Reserved MID
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: '8px', alignItems: 'center' }}>
                  <Input
                    aria-label="Correct reserved MID"
                    value={midDraft}
                    onChange={(event) => setMidDraft(event.target.value)}
                    placeholder="4754792501000836"
                    maxLength={16}
                    pattern="[0-9]{16}"
                  />
                  <Input
                    aria-label="MID correction reason"
                    value={midReason}
                    onChange={(event) => setMidReason(event.target.value)}
                    placeholder="Reason (optional)"
                  />
                  <Button
                    type="submit"
                    isLoading={updateMid.isPending}
                    disabled={updateMid.isPending || !/^\d{16}$/.test(midDraft.trim())}
                  >
                    Update MID
                  </Button>
                </div>
                {midMessage && (
                  <p style={{ fontSize: '12px', color: '#047857', marginTop: '8px' }}>{midMessage}</p>
                )}
                {midError && (
                  <p style={{ fontSize: '12px', color: '#b91c1c', marginTop: '8px' }}>{midError}</p>
                )}
              </form>
            )}
          </div>
        ) : app?.is_submitted ? (
          <div>
            <p style={{ fontSize: '14px', color: '#16a34a', fontWeight: 600, marginBottom: '8px' }}>
              Submitted to Fiserv
            </p>
            <dl style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
              <div>
                <dt style={{ color: '#6b7280' }}>Reserved MID</dt>
                <dd style={{ color: '#111827', fontFamily: 'monospace' }}>{app.fiserv_mid ?? '—'}</dd>
              </div>
              <div>
                <dt style={{ color: '#6b7280' }}>Ext Ref ID</dt>
                <dd style={{ color: '#111827', fontFamily: 'monospace' }}>{app.fiserv_ext_ref_id ?? '—'}</dd>
              </div>
              <div>
                <dt style={{ color: '#6b7280' }}>AS Ref ID</dt>
                <dd style={{ color: '#111827', fontFamily: 'monospace' }}>{app.fiserv_as_ref_id ?? '—'}</dd>
              </div>
              <div>
                <dt style={{ color: '#6b7280' }}>Submitted At</dt>
                <dd style={{ color: '#111827' }}>{app.fiserv_submitted_at ? new Date(app.fiserv_submitted_at).toLocaleString() : '—'}</dd>
              </div>
            </dl>
          </div>
        ) : (
          <p style={{ fontSize: '14px', color: '#6b7280' }}>
            Awaiting Fiserv submission. The job fires automatically when this stage is entered.
          </p>
        )}
      </SectionBody>
    </>
  )
}
